export const lessons: LessonsSetup = {
    '1.1': {
        title: 'Welcome to Startup Poker!',
        text: `<p>We're using poker as a metaphor for innovation.</p>
                <p>You will bet (invest) money in your hand (innovation) against a competitor to win the pot (revenue).</p>
                <p>To win, you will need luck <u>and</u> to make good decisions on when to play and when to fold.</p>
                <p>The rules will be similar to Texas hold 'em, but with some significant changes.</p>`,
        button1: {
            label: 'I have never played poker.',
            lesson: '1.2.1',
        },
        button2: {
            label: 'Practice with an open hand.',
            lesson: '1.3.1',
        },
        button3: {
            label: 'Practice with a closed hand.',
            lesson: '1.4.1.1',
        },
        button4: {
            color: 'secondary',
            label: "I know how to play Texas hold 'em, jump to Lesson 2.",
            lesson: '2',
        },
        annotation1: "These are your opponent's cards.",
        annotation2: 'These are the community cards.',
        annotation3: 'This is your hand - called "hole cards."',
    },
    '1.2.1': {
        title: 'Highest Hand Wins',
        text: `<p>Poker is a card game where the person with the best five card hand wins.</p>
                <p>The best possible hands are the ones that are the hardest to get (the most unlikely).</p>
                <p><b>Don't worry, we're going to show you the odds of winning and you can see this cheat sheet at any time.</b></p>`,
        button1: {
            label: 'How do I win?',
            lesson: '1.2.2',
        },
    },
    '1.2.2': {
        title: 'Five out of Seven Cards',
        text: `<p>You use the best five out of seven cards.</p>
                <p>You have two cards called "hole cards" in your hand and there are five community cards.</p>
                <p>Out of the seven cards available, <b>you have {holeHandName}.</b></p>`,
        button1: {
            label: 'How do I win?',
            lesson: '1.2.3',
        },
        button2: {
            color: 'secondary',
            label: 'Shuffle and show me another hand.',
            lesson: '1.2.2.1',
        },
    },
    '1.2.2.1': {
        title: '{title}',
        text: `<p>Out of the seven cards available, <b>you have {holeHandName}.</b></p>`,
        button1: {
            label: 'How do I win?',
            lesson: '1.2.3',
        },
        button2: {
            action: 'shuffle',
            color: 'secondary',
            label: 'Shuffle and show me another hand.',
        },
    },
    '1.2.3': {
        title: 'Opponents',
        text: `<p>Your opponent can also use their two hole cards and the community cards.</p>
                <p>Opponent 1 has three kings and two jacks - a full house.</p>
                <p><b>A full house beats three of a kind.</b></p>`,
        button1: {
            label: 'What are my odds of winning?',
            lesson: '1.2.4',
        },
        button2: {
            color: 'secondary',
            label: 'Shuffle and show me another hand.',
            lesson: '1.2.3.1',
        },
    },
    '1.2.3.1': {
        title: '{resultTitle}',
        text: `<p>Your opponent has {opponentHandName}.</p>
                <p><b>{result}</b></p>`,
        button1: {
            label: 'What are my odds of winning?',
            lesson: '1.2.4',
        },
        button2: {
            action: 'shuffle',
            color: 'secondary',
            label: 'Shuffle and show me another hand.',
        },
    },
    '1.2.4': {
        title: 'Odds of Winning',
        text: `<p>When you actually play, you will not be able to see the opponent cards.</p>
                <p>We will show you your odds of winning <i>based on the cards you can see</i>.</p>
                <p><b>Click on the info button of the <span class="nowrap">Win %</span> meter to see more detail.</b></p>`,
        button1: {
            label: "Let's play an open hand.",
            lesson: '1.3.1',
        },
        button2: {
            color: 'secondary',
            label: "Let's play a closed hand.",
            lesson: '1.4.1.1',
        },
        button3: {
            color: 'secondary',
            label: 'Skip to betting and folding.',
            lesson: '2',
        },
        annotation1: 'This is your chance of winning given what you know.',
    },
    '1.3.1': {
        title: 'Play an Open Hand',
        text: `<p>We'll play this hand open - so you will see your opponent's cards.</p>
                <p><b>Click the <i>Deal</i> button on your hole cards.</b></p>`,
    },
    '1.3.2': {
        title: 'Hole Cards',
        text: `<p>These are the hole cards.</p>
                <p>You have {holeCardNames}.</p>
                <p>Your opponent has {opponentCardNames}.</p>
                <p>We don't know what the community cards are yet, so your odds of winning are {winPercentage}%.</p>
                <p><b>Click the <i>Deal</i> button.</b></p>`,
    },
    '1.3.2.1': {
        title: 'Hole Cards',
        text: `<p>You have {holeCardNames}.</p>
                <p>Your opponent has {opponentCardNames}.</p>
                <p>Your odds of winning are {winPercentage}%.</p>
                <p><b>Click the <i>Deal</i> button.</b></p>`,
    },
    '1.3.3': {
        title: 'The Flop',
        text: `<p>In this round, three cards are dealt - called the flop.</p>
                <p>These are community cards, so they can help both you AND your opponent.</p>
                <p>Now, you have {holeHandName}.</p>
                <p>Your opponent has {opponentHandName}.</p>
                <p>Your odds of winning are {winPercentage}%.</p>
                <p><b>Click the <i>Deal</i> button.</b></p>`,
    },
    '1.3.3.1': {
        title: 'The Flop',
        text: `<p>You have {holeHandName}.</p>
                <p>Your opponent has {opponentHandName}.</p>
                <p>Your odds of winning are {winPercentage}%.</p>
                <p><b>Click the <i>Deal</i> button.</b></p>`,
    },
    '1.3.4': {
        title: 'The Turn',
        text: `<p>In this round, only one card is dealt - called the turn.</p>
                <p>You have {holeHandName}.</p>
                <p>Your opponent has {opponentHandName}.</p>
                <p>Your odds of winning are {winPercentage}%.</p>
                <p>As you get more information, your odds of winning keep changing.</p>
                <p><b>Click the <i>Deal</i> button.</b></p>`,
    },
    '1.3.4.1': {
        title: 'The Turn',
        text: `<p>You have {holeHandName}.</p>
                <p>Your opponent has {opponentHandName}.</p>
                <p>Your odds of winning are {winPercentage}%.</p>
                <p><b>Click the <i>Deal</i> button.</b></p>`,
    },
    '1.3.5': {
        title: 'The River',
        text: `<p>The last card is called the river.</p>
                <p>Once you know all the cards, you know who wins - in this case, {resultMessage}!</p>
                <p>But in a real game, you can't see your opponent's cards.</p>`,
        button1: {
            label: "Let's play a closed hand.",
            lesson: '1.4.1',
        },
        button2: {
            color: 'secondary',
            label: 'Let me practice with an open hand again.',
            lesson: '1.3.2.1',
        },
        button3: {
            color: 'secondary',
            label: "Let's jump to betting and folding.",
            lesson: '2',
        },
    },
    '1.3.5.1': {
        title: 'The River',
        text: `<p>{result}</p>
                <p>But in a real game, you can't see your opponent's cards.</p>`,
        button1: {
            label: "Let's play a closed hand.",
            lesson: '1.4.1',
        },
        button2: {
            color: 'secondary',
            label: 'Let me practice with an open hand again.',
            lesson: '1.3.2.1',
        },
        button3: {
            color: 'secondary',
            label: "Let's jump to betting and folding.",
            lesson: '2',
        },
    },
    '1.4.1': {
        title: 'Play a Closed Hand',
        text: `<p>Let's play a closed hand.</p>
                <p>We'll give you the SAME cards as your last open hand but we'll pick random cards for the opponent.</p>
                <p>They could have useless cards or a pair of aces.</p>
                <p>You will not be able to see your opponent's cards but we'll update the odds of winning based on the information you know.</p>
                <p><b>Click the <i>Deal</i> button.</b></p>`,
    },
    '1.4.1.1': {
        title: 'Play a Closed Hand',
        text: `<p>Let's play a closed hand.</p>
                <p>You will not be able to see your opponent's cards but we'll update the odds of winning based on the information you know.</p>
                <p><b>Click the <i>Deal</i> button.</b></p>`,
    },
    '1.4.1.2': {
        title: 'Play a Closed Hand',
        text: `<p>Let's play a closed hand.</p>
                <p>We'll update the odds of winning based on the information you know.</p>
                <p><b>Click the <i>Deal</i> button.</b></p>`,
    },
    '1.4.2': {
        title: 'Hole Cards',
        text: `<p>When you played open with these cards, your odds of winning were {prevWinPercentage}%.</p>
                <p>Now your odds of winning are {winPercentage}% because you don't know what cards your opponent holds.</p>
                <p>The amount of information you have adjusts the odds.</p>
                <p><b>Click the <i>Deal</i> button.</b></p>`,
    },
    '1.4.2.1': {
        title: 'Hole Cards',
        text: `<p>Your odds of winning are {winPercentage}% because you don' know what cards your opponent holds.</p>
                <p>The amount of information you have adjusts the odds.</p>
                <p><b>Click the <i>Deal</i> button.</b></p>`,
    },
    '1.4.2.2': {},
    '1.4.3': {
        title: 'The Flop',
        text: `<p>We've given you the same flop as when you played open.</p>
                <p>The last time, your odds of winning were {prevWinPercentage}%, now your odds are {winPercentage}%.</p>
                <p><b>Click the <i>Deal</i> button.</b></p>`,
    },
    '1.4.3.1': {
        title: 'The Flop',
        text: `<p>Your odds of winning are now {winPercentage}% because you have additional information.</p>
                <p><b>Click the <i>Deal</i> button.</b></p>`,
    },
    '1.4.3.2': {},
    '1.4.4': {
        title: 'The Turn',
        text: `<p>We've given you the same turn as when you played open.</p>
                <p>The last time, your odds of winning were {prevWinPercentage}%, now your odds are {winPercentage}%.</p>
                <p><b>Click the <i>Deal</i> button.</b></p>`,
    },
    '1.4.4.1': {
        title: 'The Turn',
        text: `<p>Your odds of winning are now {winPercentage}% because you have additional information.</p>
                <p><b>Click the <i>Deal</i> button.</b></p>`,
    },
    '1.4.4.2': {},
    '1.4.5': {
        title: 'The River',
        text: `<p>Your final hand is {holeHandName}.</p>
                <p>This time, we still don't know who wins because we don't know the opponent's cards.</p>
                <p>But your odds of winning are {winPercentage}%.</p>
                <p><b>Click the <i>Show</i> button to see who wins.</b></p>`,
    },
    '1.4.5.1': {
        title: 'The River',
        text: `<p>Your final hand is {holeHandName}.</p>
                <p>We still don't know who wins because we don't know the opponent's cards.</p>
                <p>But your odds of winning are {winPercentage}%.</p>
                <p><b>Click the <i>Show</i> button to see who wins.</b></p>`,
    },
    '1.4.5.2': {},
    '1.4.6': {
        title: '{resultTitle}',
        text: `<p>{results}</p>
                <p>A small change in the amount of information you have changes the odds of winning.</p>`,
        button1: {
            label: 'How does this apply in the real world?',
            lesson: '1.z',
        },
        button2: {
            color: 'secondary',
            label: 'Let me play a closed hand again.',
            lesson: '1.4.1.2',
        },
        annotation1: 'You can rewind step by step and try the same hand again here.',
    },
    '1.4.6.1': {
        title: '{resultTitle}',
        text: `<p>{results}</p>
                <p>A small change in the amount of information you have changes the odds of winning.</p>`,
        button1: {
            label: 'How does this apply in the real world?',
            lesson: '1.z',
        },
        button2: {
            color: 'secondary',
            label: 'Let me play a closed hand again.',
            lesson: '1.4.2.2',
        },
        annotation1: 'You can rewind step by step and try the same hand again here.',
    },
    '1.4.6.2': {
        title: '{resultTitle}',
        button1: {
            label: 'How does this apply in the real world?',
            lesson: '1.z',
        },
        button2: {
            color: 'secondary',
            label: 'Let me play a closed hand again.',
            lesson: '1.4.1.2',
        },
        annotation1: 'You can rewind step by step and try the same hand again here.',
    },
    '1.z': {
        sound: 'real',
        title: 'The Real World',
        image: '/assets/illustrations/real-world.png',
        imageAlt: 'Real world',
        text: `<p>In both innovation and in poker, there are things we can control (whether to play or fold) and things we cannot control (the randomness of the cards and the other player's behavior).</p>
                <p>If we understand the odds of different outcomes, we can try to predict whether we will win or lose.</p>`,
        button1: {
            label: 'What do I win?',
            lesson: '2',
        },
    },
    '2': {
        heading: 'Lesson 2',
        title: 'Making Decisions',
        text: '<p>Playing or folding is the most basic decision in poker. Should you continue to invest by betting or not?</p>',
        primaryButton: 'Play',
        image: '/assets/illustrations/intro-2.svg',
        next: '2.1',
    },
    '2.1': {
        title: 'What You Win',
        text: `<p>In poker, you win the money that everyone bets. But that isn't how innovation works.</p>
                <p>When you try a big new idea, sometimes you achieve a massive impact and sometimes you get nothing.</p>
                <p>Throwing more money at a project doesn't make the potential opportunity any bigger.</p>
                <p><b>So we won't include betting in this game.</b></p>`,
        button1: {
            label: 'So what do I win?',
            lesson: '2.2',
        },
    },
    '2.2': {
        title: 'Pot Size',
        text: `<p>In this game, the amount you can win in the pot is the amount shown by the community cards.</p>
                <p>In this example, the value is \${currentPot}.</p>
                <p><b>For innovation projects, winning can mean revenue, more productivity, or lives saved. But we'll show the value as dollars here.</b></p>`,
        button1: {
            label: 'What is the expected pot? ',
            lesson: '2.3',
        },
        button2: {
            color: 'secondary',
            label: 'Show me another hand.',
            lesson: '2.2.1',
        },
        annotation1: 'The value of the pot is the sum of the value of the community cards.',
    },
    '2.2.1': {
        title: 'Pot Size',
        text: `<p>In this example, the value is \${currentPot}.</p>`,
        button1: {
            label: 'What is the expected pot? ',
            lesson: '2.3',
        },
        button2: {
            action: 'shuffle',
            color: 'secondary',
            label: 'Show me another hand.',
        },
        annotation1: 'The value of the pot is the sum of the value of the community cards.',
    },
    '2.3': {
        title: 'Expected Pot',
        text: `<p>With a random hand, the <i>expected pot</i> is \${expectedPot}.</p>
                <p>As you turn over cards, you get more information. So the <i>current pot</i> will increase, and the <i>expected pot</i> will recalculate.</p>`,
        button1: {
            label: 'Are there costs?',
            lesson: '2.5',
        },
        button2: {
            color: 'secondary',
            label: 'How is the expected pot calculated?',
            lesson: '2.4',
        },
        annotation1: 'This is the most likely value of the pot once we see the community cards.',
    },
    '2.4': {
        title: 'Histogram',
        text: `<p>We can show the distribution of likely results using a histogram.</p>
                <p>The horizontal columns show different buckets of outcomes such as \${min}-\${max}.</p>
                <p>The vertical axis shows the likelihood of each bucket where the values in the middle are the most likely.</p>
                <p>Like many statistical distributions, this one is similar to a bell curve.</p>`,
        button1: {
            label: 'Are there costs?',
            lesson: '2.5',
        },
    },
    '2.5': {
        title: 'Costs',
        text: `<p>Each card has a cost. The more information you get, the higher the cost.</p>
                <p>So to see all the cards, you'll have to spend \${allInCost} (\${holeCost}+\${flopCost}+\${turnCost}+\${riverCost}).</p>
                <p>You need to make sure the expected value of the pot is higher than your costs.</p>`,
        button1: {
            label: "Let's play a hand.",
            lesson: '2.6.1',
        },
        button2: {
            label: 'How can I calculate expected value?',
            lesson: '2.8',
        },
    },
    '2.6.1': {
        title: "Let's Play",
        text: `<p>We'll give you \${startBudget}, enough to play at least two hands.</p>
                <p>We'll keep track of your decisions to play or fold and tell you if you are making good or bad bets.</p>
                <p><b>Choices: Pay \${holeCost} to see your hole cards or go all in to see all of the cards.</b></p>`,
        annotation1: 'This is your total budget available.',
        annotation2: 'This is any money you win.',
    },
    '2.6.1.1': {
        title: "Let's Play",
        text: `<p>You still have \${budget}, enough to play at least {hands}.</p>
                <p><b>Choices: Pay \${holeCost} to see your hole cards or go all in to see all of the cards.</b></p>`,
    },
    '2.6.2': {
        title: 'Invest Or Fold',
        text: `<p>Based on your first two cards, you need to make a decision to invest more money or fold.</p>
                <p><b>Choices: Pay for the next cards, go all in, or fold.</b></p>`,
    },
    '2.6.2.1': {},
    '2.6.3': {},
    '2.6.3.1': {},
    '2.6.4': {},
    '2.6.4.1': {},
    '2.6.6': {
        title: 'You Lose',
        text: `<p>You still have budget, so you can keep playing.</p>`,
        button1: {
            label: 'How can I calculate expected value?',
            lesson: '2.8',
        },
        button2: {
            label: 'Deal another hand.',
            lesson: '2.6.1.1',
        },
    },
    '2.6.6.1': {
        title: '{resultTitle}',
        text: `<p>You still have budget, so you can keep playing.</p>`,
        button1: {
            label: 'How can I calculate expected value?',
            lesson: '2.8',
        },
        button2: {
            label: 'Deal another hand.',
            lesson: '2.6.1.1',
        },
    },
    '2.6.7.1': {
        title: 'Not Enough Budget',
        text: `<p>You don't have enough money to keep playing.</p>
                <p>(In real Texas hold 'em, going all in works differently.)</p>
                <p>You made {correctDecisions} correct decisions out of {decisions} total decisions.</p>
                <p>You can improve your decisions by <i>calculating expected</i> value.</p>`,
        button1: {
            label: 'How can I calculate expected value?',
            lesson: '2.8',
        },
    },
    '2.6.7.2': {
        title: 'Not Enough Budget',
        text: `<p>You don't have enough money to keep playing.</p>
                <p>In Texas hold 'em poker, you can bet everything and might still win.</p>
                <p>But if a real project doesn't have enough money for development, it's simply over.</p>
                <p>You made {correctDecisions} correct decisions out of {decisions} total decisions.</p>
                <p>That's perfect!</p>`,
        button1: {
            label: 'How can I calculate expected value?',
            lesson: '2.8',
        },
        button2: {
            label: 'Do people make bad decisions?',
            lesson: '2.z',
        },
    },
    '2.7': {
        title: 'Bad Decision',
        text: `<p>You decided to {decision}.</p>
                <p>The <i>expected value</i> is \${expectedValue} (\${expectedPot} × {winPercentage}%).</p>
                <p>The <i>expected cost</i> to finish the hand is \${remainingCosts}, so this was a bad decision.</p>
                <p>Are you sure you want to continue?</p>`,
        button1: {
            label: 'How can I calculate expected value? ',
            lesson: '2.8',
        },
        button2: {
            action: 'play',
            color: 'secondary',
            label: 'Yes, let me play.',
        },
    },
    '2.8': {
        title: 'Expected Value',
        text: `<p>To calculate the <i>expected value</i>, you multiply the expected pot by the likelihood of winning.</p>
                <p><i>Expected Value =</i> \${expectedValue} (\${expectedPot} × {winPercentage}%).</p>
                <p>Since the cost is \${allInCost} to play one complete hand, is playing the hand a good bet?</p>
                <p><b>Choices: Click on the yellow buttons to either go all in or fold.</b></p>`,
        button1: {
            color: 'secondary',
            label: 'I can calculate expected values, skip this please.',
            lesson: '2.z',
        },
    },
    '2.9.1': {
        title: "Correct - Don't Go All In",
        text: `<p>Paying \${allInCost} to play one hand doesn't make sense. Never bet everything without seeing any information.</p>
                <p>If the cost is the same, it's better to allocate resources into many small decisions rather than one big decision you can't change.</p>`,
        button1: {
            label: 'What about deciding on just one card?',
            lesson: '2.10',
        },
        button2: {
            color: 'secondary',
            label: 'I can calculate expected values, skip this please.',
            lesson: '2.z',
        },
    },
    '2.9.2': {
        title: 'Oops - Why go all in?',
        text: `<p>Imagine you can pay \${allInCost} for a seven course meal or you can incrementally pay for each course before it is served.</p>
                <p>If you pay in full up front, you are stuck - even if the chef is terrible.</p>
                <p>If you pay one course at a time and the first two courses are terrible, you could leave and spend your money elsewhere.</p>`,
        button1: {
            label: 'What about deciding on just one card?',
            lesson: '2.10',
        },
    },
    '2.10': {
        title: 'One Unknown Card',
        text: `<p>Let's go backwards. Here, we have already paid to see most of the cards.</p>
                <p>The expected pot is \${expectedPot} and you have a {winPercentage}% chance of winning:</p>
                <p>Should you pay \${riverCost} to see the last card?</p>
                <p><b>Choices: Click to pay for the last card or fold.</b></p>`,
        button1: {
            color: 'secondary',
            label: 'I can calculate expected values, skip this please.',
            lesson: '2.z',
        },
        annotation1: 'Pay the cost for the next card only.',
    },
    '2.11': {
        title: 'Correct!',
        text: `<p>We knew the expected pot was worth \${prevExpectedPot} and we had a {prevWinPercentage}% chance of winning.</p>
                <p><i>Expected Value</i> is \${prevExpectedValue} = \${prevExpectedPot} × {prevWinPercentage}%.</p>
                <p>The cost was only \${riverCost}. So this was a good bet.</p>`,
        button1: {
            label: 'What if I can only see the flop?',
            lesson: '2.13',
        },
        button2: {
            color: 'secondary',
            label: 'Show me with a different hand.',
            lesson: '2.10',
        },
    },
    '2.11.1': {
        title: 'Fold. Sure?',
        text: `<p>We knew the expected pot was worth \${prevExpectedPot} and we had a {prevWinPercentage}% chance of winning.</p>
                <p><i>Expected Value</i> is \${prevExpectedValue} = \${prevExpectedPot} × {prevWinPercentage}%.</p>
                <p>The cost was only \${riverCost}. So it was a good bet.</p>`,
        button1: {
            label: 'Let me try again',
            lesson: '2.10',
        },
        button2: {
            color: 'secondary',
            label: 'What about the money I already spent?',
            lesson: '2.12',
        },
    },
    '2.12': {
        title: 'Sunk Cost',
        text: `<p>Money you already spent is called sunk cost and should <u>not</u> be considered in the decision.</p>
                <p>If you pay \${allInCost} to see a movie, that money is now gone, no refunds.</p>
                <p>If the first 30 minutes of a 3 hour movie are terrible, you <u>now</u> have the option to get up and do something more fun with your life for 2.5 hours.</p>
                <p>In this case, you <u>now</u> have a {prevWinPercentage}% chance of winning \${prevExpectedPot} if you spend \${riverCost}. So you should play.</p>`,
        button2: {
            label: 'Show me with a different hand.',
            lesson: '2.10',
        },
    },
    '2.13': {
        title: 'Five Cards',
        text: `<p>We know the pot is worth \${currentPot} so far and we have a {winPercentage}% chance of winning.
                <p>Expected Value = \${expectedValue} = \${expectedPot} × {winPercentage}%
                <p><b>Choices: Click to go all in for \${turnRemainingCosts}, pay for the turn for \${turnCost}, or fold.</b></p>`,
    },
    '2.14': {
        title: 'Pay for the Turn',
        text: `<p><b>Paying for one card for \${turnCost} is a good idea.</b></p>
                <p>If you pay \${turnCost} to see one card and it's terrible, you can fold. That saves you from having to spend another \${riverCost} and you can spend it on a different hand.<p>
                <p>By investing money (resources) step by step and <i>being willing to stop</i>, you save money in the long run.</p>
                <p>To put it simply, the \${turnCost} buys you information to make decisions.</p>`,
        button1: {
            label: 'What if I can only see my hole cards?',
            lesson: '2.15',
        },
        button2: {
            color: 'secondary',
            label: 'Show me with a different hand.',
            lesson: '2.13',
        },
    },
    '2.14.1': {
        title: 'Go All In',
        text: `<p>If you pay \${turnCost} to see one card and it's terrible, you can fold. That saves you \${riverCost} that you can spend on a different hand.</p>
                <p><b>If you spend \${turnRemainingCosts} right away, you are ignoring early signals.</b></p>
                <p>By investing money (resources) step by step and <i>being willing to stop</i>, you save money in the long run.</p>`,
        button1: {
            label: 'Show me with a different hand.',
            lesson: '2.13',
        },
    },
    '2.14.2': {
        title: 'Fold',
        text: `<p>You had a {prevWinPercentage}% chance of winning and the expected value of the pot was \${prevExpectedPot}.</p>
                <p>That makes the Expected Value \${prevExpectedPot} × {prevWinPercentage}% = \${prevExpectedValue}</p>
                <p>Either going all in (\${turnRemainingCosts}) or paying for the turn (\${turnCost}) would have been worth it.</p>
                <p><b>So folding was a mistake.</b></p>`,
        button1: {
            label: 'Let me try again',
            lesson: '2.13',
        },
    },
    '2.15': {
        title: 'Two Cards',
        text: `<p>With just your hole cards, it can be hard to calculate the odds.</p>
                <p>Real poker players have to memorize the odds and also calculate the odds of their opponents folding.</p>
                <p><b>In this case, our opponent is a very basic player that always stays all in.</b></p>
                <p><b>Choices: Click to go all in for \${flopRemainingCosts}, pay for the flop for \${flopCost}, or fold.</b></p>`,
    },
    '2.16': {
        title: 'Pay for The Flop',
        text: `<p><b>Paying \${flopCost} to see the three community cards is a good idea.</b></p>
                <p>If you pay \${flopCost} to see the flop and it's terrible, you can fold. That saves you \${turnRemainingCosts} you can spend on a different hand.</p>
                <p>By investing step by step and being willing to stop, you save resources in the long run.</p>
                <p>To put it simply, the \${flopCost} buys you information to make decisions.</p>`,
        button1: {
            label: "What if I haven't seen any cards?",
            lesson: '2.17',
        },
        button2: {
            color: 'secondary',
            label: 'Show me with a different hand.',
            lesson: '2.15',
        },
    },
    '2.16.1': {
        title: 'Go All In',
        text: `<p>Remember, your opponent is not smart and always stays in. So you can't bluff right now.</p>
                <p>If you pay \${flopCost} to see the flop and it's terrible, you can fold. That saves you \${turnRemainingCosts} you can spend on a different hand.</p>
                <p>If you spend \${communityCost} right away, you are ignoring potential information.</p>
                <p>By investing money (resources) step by step and <i>being willing to stop</i>, you save money in the long run.</p>`,
        button1: {
            label: 'Show me with a different hand.',
            lesson: '2.15',
        },
    },
    '2.16.2': {
        title: 'Fold',
        text: `<p>You had a {prevWinPercentage}% chance of winning and the expected value of the pot is \${prevExpectedPot}.</p>
                <p>That makes the expected value \${prevExpectedValue} = \${prevExpectedPot} × {prevWinPercentage}%</p>
                <p>Going all in (\${flopRemainingCosts}) would have been a bad idea, but you could have paid (\${flopCost}) to see most of the cards.</p>
                <p>So folding was a mistake.</p>`,
        button1: {
            label: 'Let me try again.',
            lesson: '2.15',
        },
    },
    '2.17': {
        title: 'Zero Information',
        text: `<p>"When you know nothing, anything will tell you something." - Douglas Hubbard</p>
                <p>We don't actually know the value of the pot but we know we have a {winPercentage}% chance of winning.</p>
                <p>Expected Value = \${expectedPot} × {winPercentage}%</p>
                <p><b>Choices: Click to go all in for \${holeRemainingCosts}, pay \${holeCost} for your hole cards, or fold.</b></p>`,
    },
    '2.18': {
        title: 'Pay for the Hole Cards',
        text: `<p><b>Paying \${holeCost} to see the two hole cards is a good idea.</b></p>
                <p>If they are terrible, you can fold. That saves you \${flopRemainingCosts} you can spend on a different hand.</p>
                <p>By investing step by step and <i>being willing to stop</i>, you save resources in the long run.</p>
                <p>To put it simply, the \${holeCost} buys you information to make decisions.</p>`,
        button1: {
            label: 'How does it apply in the real world?',
            lesson: '2.z',
        },
        button2: {
            color: 'secondary',
            label: 'Show me with a different hand.',
            lesson: '2.17',
        },
    },
    '2.18.1': {
        title: 'Go All In',
        text: `<p>If you pay \${holeCost} to see your cards and your odds of winning go down, you can fold. That is \${communityCost} that you did <u>not</u> spend that you can spend on a different hand.</p>
                <p>If you spend \${allInCost} right away, you are ignoring potential information.</p>
                <p><b>By investing money (resources) step by step and <i>being willing to stop</i> you save money in the long run.</b></p>`,
        button1: {
            label: 'Show me with a different hand.',
            lesson: '2.17',
        },
    },
    '2.18.2': {
        title: 'Fold',
        text: `<p>Well, you can't win if you don't play.</p>
                <p>It's absolutely true that sometimes, innovation is not a good idea.</p>
                <p>If you have better options - maybe you shouldn't invest in risky things at all!</p>
                <p>But most of life has risk, so you'd better learn how to deal with it when it comes.</p>
                <p><b>Choices: Click to go all in for \${holeRemainingCosts}, pay \${holeCost} for your hole cards.</b></p>`,
    },
    '2.z': {
        sound: 'real',
        title: 'The Real World',
        image: '/assets/illustrations/real-world.png',
        imageAlt: 'Real world',
        text: `<p>Many large organizations fully fund projects in advance for several years. This is sometimes called <i>Entitlement Funding</i> because teams think they are entitled to keep going, regardless of their results.</p>
                <p>Investors think that if they stop, their initial investment would be wasted. This is called the <i>Sunk Cost Fallacy</i>.</p>
                <p>Don't throw good money at bad ideas. If your odds are going down, you should <u>stop</u>.</p>
                <p>Making many small go / no-go decisions instead of making one big bet is called <i>Metered Funding</i>.</p>`,
        button1: {
            label: 'What if I have a portfolio of investments?',
            lesson: '3',
        },
    },
    '3': {
        heading: 'Lesson 3',
        title: 'Portfolio Strategy',
        text: '<p>Is it better to make a small number of big decisions or a large number of small decisions?</p>',
        primaryButton: 'Play',
        image: '/assets/illustrations/intro-3.svg',
        next: '3.1',
    },
    '3.1': {
        title: 'All-In Strategy',
        text: `<p>We know the expected value of one hand is \${expectedValue} = \${expectedPot} × {winPercentage}%.</p>
                <p>We know playing one hand for \${allInCost} is a bad investment.</p>
                <p>If you have a budget of \${largeBudget}, you have enough to play 10 hands completely. (We won't allow reinvesting your winnings yet.)</p>
                <p>Let's play ten hands using the <i>all-in strategy</i> and look at the long-term results.</p>`,
        button1: {
            label: 'Play ten hands.',
            lesson: '3.2',
        },
    },
    '3.2': {
        title: 'First Trial of All-In',
        text: `<p><b>That didn't go too well</b>, but there will be a lot of variation, so let's try again and see how that compares.</p>`,
        button1: {
            label: 'Ok, try again.',
            lesson: '3.3',
        },
        button2: {
            color: 'secondary',
            label: 'I know this, show me the hurricane graph.',
            lesson: '3.4',
        },
    },
    '3.3': {
        title: 'Second Trial of All-In',
        text: `<p><b>A very different result.</b></p>
                <p>We can visualize all the possible results of playing this strategy by trialing a few thousand times - this is called a Monte Carlo simulation.</p>
                <p>We can visualize it with a hurricane graph.</p>`,
        button1: {
            label: 'Show me the hurricane graph.',
            lesson: '3.4',
        },
        button2: {
            action: 'retry',
            color: 'secondary',
            label: 'Try again.',
            lesson: '3.3',
        },
    },
    '3.4': {
        title: 'All-In Hurricane Graph',
        text: `<p>In a hurricane graph each color shows the likelihood of the real result landing in that area.</p>
                <p>
                    <green>GREEN</green> - Most likely outcome <br/>
                    <yellow>YELLOW</yellow> - Less likely<br/>
                    <orange>ORANGE</orange> - Unlikely<br/>
                    <red>RED</red> - Highly unlikely<br/>
                </p>
                <p>So overall, the all-in strategy gives us an average outcome of \${mean}.</p>
                <p>In other words, we lose \${loss} on average.</p>`,
        button1: {
            label: "What's the alternative?",
            lesson: '3.5',
        },
    },
    '3.5': {
        title: 'Metered Funding Strategy',
        text: `<p>Instead of paying \${allInCost} to see all cards, we'll make decisions to play or fold at each step.</p>
                <p>For example, instead of paying \${largeBudget} for ten complete hands, you could pay \${holeCosts} to see fifteen hands of hole cards and <u>then</u> invest your remaining \${remainingBudget} to play the best five hole cards all the way through.</p>
                <p>The means more chances to start with good cards.</p>`,
        button1: {
            label: "Let's try metered funding.",
            lesson: '3.6',
        },
    },
    '3.6': {
        title: 'First Trial of Metered Funding',
        text: `<p>In the first trial, you wound up playing {hands} hands, because you folded a few times.</p>
                <p>But the overall outcome wasn't great.</p>`,
        button1: {
            label: 'Show another try.',
            lesson: '3.7',
        },
        button2: {
            color: 'secondary',
            label: 'Show me the full hurricane graph.',
            lesson: '3.8',
        },
    },
    '3.7': {
        title: 'Second Trial of Metered Funding',
        text: `<p>In the {trial} trial, you wound up playing {hands} hands, because you folded a few times.</p>
                <p>The overall outcome was much better.</p>`,
        button1: {
            label: 'Show me the full hurricane graph.',
            lesson: '3.8',
        },
        button2: {
            action: 'retry',
            color: 'secondary',
            label: 'Try again.',
            lesson: '3.7',
        },
    },
    '3.8': {
        title: 'Metered Funding Hurricane Graph',
        text: `<p>Here's the metered funding strategy over time.</p>
                <p>Even though we may play fewer hands to the end, we play more hands overall and only keep playing when the cards look good.</p>
                <p>This gives us an average outcome of \${mean}.</p>
                <p><b>We are only putting more money in when the odds are in our favor.</b></p>`,
        button1: {
            label: 'How does this compare to all-in?',
            lesson: '3.9',
        },
    },
    '3.9': {
        title: 'Comparison',
        text: `<p>Here are the two strategies compared to one another.</p>
                <p>Average outcome of all-in: \${allInMean}</p>
                <p>Average outcome of metered funding: \${meteredFundingMean}</p>
                <p>Using metered funding to gain information is a better strategy.</p>`,
        button1: {
            label: "What if you don't have enough money for a portfolio strategy?",
            lesson: '3.11',
        },
    },
    '3.11': {
        title: 'Only One Bet',
        text: `<p>Of course, if you have only \${allInCost} <b>and can only play one hand</b>, the decision changes.</p>
                <p>It's still better to pay \${holeCost} for the first two cards and then fold if they are bad because you can save the rest of your money.</p>
                <p>But if you <u>must</u> earn \${earn}, then you only have on option - go all in!</p>
                <p>You can never earn \${earn} if you fold.</p>`,
        button1: {
            label: 'How does this work in the real world? ',
            lesson: '3.z',
        },
    },
    '3.z': {
        sound: 'real',
        title: 'The Real World',
        image: '/assets/illustrations/real-world.png',
        imageAlt: 'Real world',
        text: `<p>If you only have the resources for one attempt and you have to deliver a result, then it never makes sense to stop the project.</p>
                <p>If you have a big budget to spread out across a portfolio of different projects, then it's better to use <i>Metered Funding</i>, make small bets, and stop projects early.</p>
                <p>This is why project teams, who only see their budget, almost <u>always</u> want to keep going.</p>
                <p>But if you take a wider portfolio perspective, it's better to shut down unpromising projects and place more bets.</p>`,
        button1: {
            label: 'Can I cheat?',
            lesson: '4',
        },
    },
    '4': {
        heading: 'Lesson 4',
        title: 'Buying Information',
        text: '<p>Information has value - but some information is worth more than others.</p>',
        primaryButton: 'Play',
        image: '/assets/illustrations/intro-4.svg',
        next: '4.1',
    },
    '4.1': {
        title: 'Buying Information',
        text: `<p>A good way to change the odds is to know more than your opponent.</p>
                <p>Good poker players try to detect what cards an opponent has by seeing how they play.</p>
                <p>In this game, we'll just spy on them.</p>
                <p><b>Pay \${holeCost} for your hole cards first.</b></p>`,
    },
    '4.5': {
        title: 'Spy on Your Opponent',
        text: `<p>If you can see your opponent's cards, you can better calculate your odds of success.</p>
                <p>That would be a huge advantage.</p>
                <p><b>Watch your odds and pay \${spyOpponentCost} to spy on your opponent.</b></p>`,
        button1: {
            color: 'secondary',
            label: 'I get it - show the hurricane graph.',
            lesson: '4.7',
        },
        annotation1: 'Sneak a peek.',
    },
    '4.6': {
        title: 'Easy Decision',
        text: `<p>Knowing your opponent's cards helps you calculate the odds of winning exactly at {winPercentage}%.</p>
                <p>That makes the <i>expected value</i> \${expectedValue} = \${expectedPot} × {winPercentage}%</p>
                <p>Now you can make better decisions to play or fold.</p>`,
        button1: {
            label: 'Show me the long term advantage.',
            lesson: '4.7',
        },
    },
    '4.7': {
        title: 'Hurricane Graph',
        text: `<p>This strategy gives us a big advantage over our opponent.</p>
                <p>But sometimes it actually is <u>not</u> worth paying for the information.</p>
                <p>If we just have bad cards, there is very little point in paying to look at our opponent's cards - we should just fold.</p>`,
        button1: {
            label: "What's the value of information?",
            lesson: '4.z',
        },
    },
    '4.z': {
        sound: 'real',
        title: 'The Real World',
        image: '/assets/illustrations/real-world.png',
        imageAlt: 'Real world',
        text: `<p>Information has value - but some information is worth more than others.<p>
                <p>We have a limited budget, so it's better to only buy information when the value improves decision making.<p>
                <p>If the information will not change your decision (to fold <u>or</u> to stay in) - the information is not worth it.<p>`,
        button1: {
            label: 'What if there are more opponents?',
            lesson: '5',
        },
    },
    '5': {
        heading: 'Lesson 5',
        title: 'Competition',
        text: '<p>The amount of competition radically impacts your odds of success.</p>',
        primaryButton: 'Play',
        image: '/assets/illustrations/intro-5.svg',
        next: '5.1',
    },
    '5.1': {
        title: 'Competition',
        text: `<p>Everything changes when you have competition.</p>
                <p>Your odds of winning in a 1:1 battle are 50%.</p>`,
        button1: {
            label: 'What about two opponents?',
            lesson: '5.2',
        },
    },
    '5.2': {
        title: 'Three Players',
        text: `<p>When there are two opponents, your odds go down to 33.3%.</p>
                <p>Even with a metered funding strategy, you will have a harder time winning.</p>`,
        button1: {
            label: 'What about three opponents?',
            lesson: '5.3',
        },
    },
    '5.3': {
        title: 'Four Players',
        text: `<p>When there are four players, your odds go down to 25%.</p>
                <p>Even if all the players are simple robots and you can spy on them, winning is harder.</p>
                <p><b>Pay \${holeCost} for your cards and start playing.</b></p>`,
    },
    '5.4': {
        title: 'Great Cards',
        text: `<p><b>Buy the flop for \${flopCost}.</b></p>`,
    },
    '5.5': {
        title: 'Surprise!',
        text: `<p>In poker, joining late is not allowed.</p>
                <p>But in this game, sometimes players can join the game late when they see that there is a sizable pot.</p>`,
        button1: {
            label: 'Is that fair?',
            lesson: '5.z',
        },
    },
    '5.z': {
        promo: true,
        sound: 'real',
        title: 'The Real World',
        image: '/assets/illustrations/real-world.png',
        imageAlt: 'Real world',
        text: `<p>What does fair have to do with it?</p>
                <p>Weren't you just peeking at other people's cards a minute ago?</p>
                <p>Poker is just a metaphor, but the world isn't fair and not everyone is playing by the same rules.</p>
                <p>Our advantages can be swiftly copied and maybe someone is looking at our cards.</p>
                <p>Someone can easily enter the game late and catch up.</p>`,
        button1: {
            label: 'Continue to Practice',
            lesson: '6',
        },
    },
    '6': {
        heading: 'Practice',
        title: 'Startup Poker',
        text: "<p>Time to play! Use what you've learned to make the best decisions possible.</p>",
        primaryButton: 'Play',
        secondaryButton: 'Learn how to play',
        image: '/assets/illustrations/intro-6.svg',
        next: '6.1',
    },
    '6.1': {},
    '6.x.1': {
        promo: true,
        title: 'Great Job!',
        text: `<p>You're doing well and earning money!</p>
                <p>You can only play ten hands per day, but come back tomorrow and we'll keep your budget saved.</p>`,
    },
    '6.x.2': {
        promo: true,
        title: 'Not Bad!',
        text: `<p>You're close to break even.</p>
                <p>You can only play ten hands per day, but come back tomorrow and we'll keep your budget saved.</p>`,
    },
    '6.x.3': {
        promo: true,
        title: 'Out of Luck',
        text: `<p>Sorry!</p>
                <p>You've run out of cash.</p>
                <p>Try again tomorrow and we'll refill your budget.</p>`,
    },
};

export const lessonKeys = Object.keys(lessons).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
);
export const lessonParents = Array.from(new Set(lessonKeys.map(key => parseInt(key))));
